// Thin client for the VAE Cloud Function. Firebase is imported lazily so it only
// loads when the visitor actually opens VAE (keeps it off the critical path), and
// so the app builds/runs even before the backend is configured.

import { FIREBASE_CONFIG, RECAPTCHA_SITE_KEY, FUNCTIONS_REGION, VAE_CONFIGURED } from "./config";
import { enableAppCheckDebugToken } from "../firebaseDebug";

export type VaeTurn = { role: "user" | "assistant"; content: string };

// Named Firebase app for VAE — keeps it distinct from the "site" app (website
// backend), since both projects are initialized on the same page.
const APP_NAME = "vae";

export class VaeError extends Error {
  constructor(
    message: string,
    public code: string = "unknown",
  ) {
    super(message);
  }
}

let callablePromise: Promise<(history: VaeTurn[]) => Promise<string>> | null = null;

// Build (once) the function that sends a conversation and returns VAE's reply.
async function getCallable(): Promise<(history: VaeTurn[]) => Promise<string>> {
  if (!VAE_CONFIGURED) {
    throw new VaeError("VAE isn't connected yet.", "unconfigured");
  }
  if (!callablePromise) {
    callablePromise = (async () => {
      const { initializeApp, getApps } = await import("firebase/app");
      const { initializeAppCheck, ReCaptchaV3Provider } = await import("firebase/app-check");
      const { getFunctions, httpsCallable } = await import("firebase/functions");

      // Named app ("vae") — the site also runs a "site" app for the website
      // backend, so neither may claim the default app. Reuse if already created.
      const existing = getApps().find((a) => a.name === APP_NAME);
      const app = existing ?? initializeApp(FIREBASE_CONFIG, APP_NAME);

      // Local dev only — see firebaseDebug.ts. Stripped from production builds.
      enableAppCheckDebugToken(process.env.REACT_APP_VAE_APPCHECK_DEBUG_TOKEN);

      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(RECAPTCHA_SITE_KEY),
        isTokenAutoRefreshEnabled: true,
      });

      const fn = httpsCallable<{ messages: VaeTurn[] }, { reply: string }>(
        getFunctions(app, FUNCTIONS_REGION),
        "vaeChat",
      );

      return async (history: VaeTurn[]) => {
        const res = await fn({ messages: history });
        return res.data.reply;
      };
    })();
  }
  return callablePromise;
}

/** Send the conversation to VAE and return the assistant reply text. */
export async function askVae(history: VaeTurn[]): Promise<string> {
  try {
    const call = await getCallable();
    return await call(history);
  } catch (e) {
    // Firebase callable errors carry a `code` like "functions/resource-exhausted".
    const err = e as { code?: string; message?: string };
    if (err instanceof VaeError) throw err;
    const code = (err.code ?? "").replace(/^functions\//, "");
    const message =
      code === "resource-exhausted"
        ? err.message ?? "VAE is at its limit right now. Please try again later."
        : "VAE couldn't reach the server. Please try again in a moment.";
    throw new VaeError(message, code || "network");
  }
}
